import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { gql, useApolloClient } from '@apollo/client';
import { useMsal } from '@azure/msal-react';

// Five states from docs/amends/user-access-restriction/remediation-plan.md §4.5.
// AUTHENTICATING is owned upstream by MsalAuthenticationTemplate/PageSpinner
// (frontend/src/index.js) - by the time this provider mounts, MSAL has already
// authenticated, so this provider only ever starts at VERIFYING_ACCESS. It is
// still named here so all five states are represented in one place.
export const ACCESS_GATE_STATE = {
    AUTHENTICATING: 'AUTHENTICATING',
    VERIFYING_ACCESS: 'VERIFYING_ACCESS',
    ACCESS_GRANTED: 'ACCESS_GRANTED',
    ACCESS_DENIED: 'ACCESS_DENIED',
    ACCESS_CHECK_FAILED: 'ACCESS_CHECK_FAILED'
};

const CURRENT_USER_ACCESS_QUERY = gql`
    query CurrentUserAccess {
        currentUserAccess {
            accessStatus
            reasonCode
            isSystemUser
            correlationId
        }
    }
`;

const AccessContext = createContext({
    state: ACCESS_GATE_STATE.VERIFYING_ACCESS,
    accessStatus: null,
    reasonCode: null,
    correlationId: null,
    retry: () => {}
});

export const AccessContextProvider = ({ children }) => {
    const client = useApolloClient();
    const { accounts } = useMsal();

    const [state, setState] = useState(ACCESS_GATE_STATE.VERIFYING_ACCESS);
    const [accessStatus, setAccessStatus] = useState(null);
    const [reasonCode, setReasonCode] = useState(null);
    // Real, backend-generated correlation ID (docs/amends/user-access-restriction
    // /tasks.md P7-6) - can be cross-referenced against the backend's own
    // access-control log lines, unlike a client-only breadcrumb.
    const [correlationId, setCorrelationId] = useState(null);

    // Guards against a stale in-flight response overwriting a newer decision
    // (rapid account switch dispatching two overlapping checks).
    const requestSeq = useRef(0);

    const checkAccess = useCallback(async () => {
        const thisRequestId = ++requestSeq.current;
        setState(ACCESS_GATE_STATE.VERIFYING_ACCESS);

        try {
            const { data, errors } = await client.query({
                query: CURRENT_USER_ACCESS_QUERY,
                fetchPolicy: 'network-only',
                errorPolicy: 'all'
            });

            if (thisRequestId !== requestSeq.current) {
                return; // superseded by a newer check - ignore this result
            }

            if (errors && errors.length > 0) {
                setAccessStatus(null);
                setReasonCode(errors[0].extensions?.code || null);
                setCorrelationId(null);
                setState(ACCESS_GATE_STATE.ACCESS_CHECK_FAILED);
                return;
            }

            const result = data && data.currentUserAccess;
            if (!result || !result.accessStatus) {
                setAccessStatus(null);
                setReasonCode(null);
                setCorrelationId(null);
                setState(ACCESS_GATE_STATE.ACCESS_CHECK_FAILED);
                return;
            }

            setAccessStatus(result.accessStatus);
            setReasonCode(result.reasonCode);
            setCorrelationId(result.correlationId || null);

            if (result.accessStatus === 'ACCESS_GRANTED') {
                setState(ACCESS_GATE_STATE.ACCESS_GRANTED);
            } else if (result.accessStatus === 'ACCESS_VERIFICATION_UNAVAILABLE') {
                setState(ACCESS_GATE_STATE.ACCESS_CHECK_FAILED);
            } else {
                setState(ACCESS_GATE_STATE.ACCESS_DENIED);
            }
        } catch (err) {
            if (thisRequestId !== requestSeq.current) {
                return;
            }
            setAccessStatus(null);
            setReasonCode(null);
            setCorrelationId(null);
            setState(ACCESS_GATE_STATE.ACCESS_CHECK_FAILED);
        }
    }, [client]);

    const activeAccount = accounts && accounts.length > 0 ? accounts[0] : null;
    // Identify the signed-in identity by its token claim (oid), not just the
    // MSAL account object reference, so the gate re-runs on initial load, a
    // full page refresh, an account switch, AND a silent token renewal that
    // actually changes identity claims - but not on every routine silent
    // token refresh for the *same* identity (which would otherwise re-verify
    // on nearly every API call and thrash the UI between VERIFYING_ACCESS and
    // ACCESS_GRANTED).
    const identityKey = activeAccount
        ? (activeAccount.idTokenClaims?.oid || activeAccount.homeAccountId || activeAccount.localAccountId)
        : null;

    useEffect(() => {
        if (identityKey) {
            checkAccess();
        }
    }, [identityKey, checkAccess]);

    return (
        <AccessContext.Provider value={{ state, accessStatus, reasonCode, correlationId, retry: checkAccess }}>
            {children}
        </AccessContext.Provider>
    );
};

export const useAccessGate = () => useContext(AccessContext);
